import Swal from 'sweetalert2'
import { toast } from 'react-toastify'

import { escapeHtml } from '../../../../shared/helpers'
import http from '../../../../shared/http'
import { TApi, TRoles } from '../../../../shared/const'

/*
 * Konto einer Schüler:in löschen (Anforderung #226, Löschwunsch nach DSGVO).
 *
 * Eine Stelle für die Liste (Drei-Punkte-Menü) und das Profilfenster (Reiter
 * „Handlungen") — beide bekommen ihre Handlungen aus `getActions` in
 * `views/admin/Users`.
 *
 * Ablauf: erst fragen, was das Löschen mitnähme (`GET …/removal`, ändert
 * nichts), dann das Fenster mit Name, Junior Company, was gelöscht wird, was
 * bleibt, Hinweisen — und zum Bestätigen den Nachnamen eintippen. Was der
 * Server verweigert (einzige Schüler:in der Junior Company, unerwartete
 * Verweise), zeigt ein eigenes Fenster ohne Löschknopf.
 *
 * **Geschützt wird im Backend** (`services/studentRemoval`): Bundesland-Grenze,
 * Rolle, Sperren. Diese Datei sorgt nur dafür, dass kein Knopf erscheint, den
 * der Server verweigern würde.
 */

/** Wer den Eintrag sieht: Landes- und Bundesadmin, nur bei Schüler:innen. */
export const canRemoveStudent = (viewerRole, user) =>
	[TRoles.ADMIN, TRoles.FEDERAL_ADMIN].includes(viewerRole) && user?.role === TRoles.STUDENT

const fullName = person => [person?.firstName, person?.lastName].filter(Boolean).join(' ')

/*
 * Sperren und Hinweise kommen vom Server mit einem Code. Die bekannten werden
 * hier übersetzt (die Sätze des Servers sind deutsch); unbekannte zeigen den
 * Satz des Servers.
 */
const messageFor = (t, item, preview) => {
	const name = fullName(preview.student)
	const company = preview.company?.name || ''
	switch (item.code) {
		case 'LAST_STUDENT':
			return t('studentRemovalLastStudent', { name, company })
		case 'CEO':
			return t('studentRemovalCeo', { name, company })
		case 'BELOW_TWO':
			return t('studentRemovalBelowTwo', { company, count: Math.max(0, (preview.studentsInCompany || 0) - 1) })
		case 'NOT_A_STUDENT':
			return t('studentRemovalNotAStudent')
		default:
			return item.message
	}
}

const sum = values => Object.values(values || {}).reduce((total, value) => total + (Number(value) || 0), 0)

/** Was gelöscht wird — eine Zeile je Art, mit Zahl, nur was es gibt. */
export const deletedLines = (t, preview) => {
	const d = preview.deleted || {}
	const lines = [
		[d.documents, t('studentRemovalItemDocuments')],
		[d.Notification, t('studentRemovalItemNotifications')],
		[d.CourseProgress, t('studentRemovalItemCourse')],
		[(d.CompanyConceptAssignment || 0) + (d.CompanyConceptExemption || 0), t('studentRemovalItemConcept')],
		[d.invitations, t('studentRemovalItemInvitations')],
		[d.userComments, t('studentRemovalItemComments')],
		[(d.Permission || 0) + (d.AdminTraining || 0) + (d.AdminReminderSetting || 0), t('studentRemovalItemSettings')]
	]
		.filter(([count]) => Number(count) > 0)
		.map(([count, label]) => `${label}: ${count}`)

	// Konto und Profilbild gehen immer — sie stehen zuerst.
	return [t('studentRemovalItemAccount'), ...lines]
}

export const confirmationHtml = (t, preview) => {
	const name = escapeHtml(fullName(preview.student))
	const company = preview.company ? escapeHtml(preview.company.name) : null
	const detached = sum(preview.detached)
	const warnings = (preview.warnings || []).map(item => escapeHtml(messageFor(t, item, preview)))

	return [
		`<div style="text-align:left">`,
		`<p><b>${name}</b> · ${company ? `„${company}“` : escapeHtml(t('studentRemovalNoCompany'))}</p>`,
		`<p style="margin-bottom:4px">${escapeHtml(t('studentRemovalDeletedHeading'))}</p>`,
		`<ul style="margin:0 0 12px 18px;padding:0">`,
		...deletedLines(t, preview).map(line => `<li>${escapeHtml(line)}</li>`),
		`</ul>`,
		`<p>${escapeHtml(t('studentRemovalKept', { count: detached }))}</p>`,
		...(warnings.length
			? [
					`<div style="background:#fff4e5;border-left:4px solid #f0a020;padding:8px 10px;margin:10px 0">`,
					...warnings.map(text => `<p style="margin:4px 0">${text}</p>`),
					`</div>`
			  ]
			: []),
		`<p><b>${escapeHtml(t('studentRemovalFinal'))}</b></p>`,
		`<p>${escapeHtml(t('studentRemovalTypeLastName', { lastName: preview.student?.lastName || '' }))}</p>`,
		`</div>`
	].join('')
}

/** Stimmt die Eingabe mit dem Nachnamen überein? Leerzeichen und Groß/klein zählen nicht. */
export const lastNameMatches = (input, lastName) =>
	Boolean(lastName) &&
	String(input || '')
		.trim()
		.toLocaleLowerCase('de') === String(lastName).trim().toLocaleLowerCase('de')

/**
 * Der ganze Ablauf. `onDone` läuft nur nach erfolgreichem Löschen.
 * @returns {Promise<boolean>} ob gelöscht wurde
 */
export const removeStudentAccount = async ({ t, user, onDone }) => {
	let preview
	try {
		;({ data: preview } = await http.get(TApi.ADMIN_STUDENT_REMOVAL_PREVIEW(user.id)))
	} catch {
		return false // Meldung kommt aus shared/http
	}

	if (preview.blockers?.length) {
		await Swal.fire({
			title: t('studentRemovalBlockedTitle'),
			html: `<div style="text-align:left">${preview.blockers
				.map(item => `<p>${escapeHtml(messageFor(t, item, preview))}</p>`)
				.join('')}</div>`,
			icon: 'error',
			confirmButtonText: t('OK')
		})
		return false
	}

	const lastName = preview.student?.lastName || ''
	const { isConfirmed } = await Swal.fire({
		title: t('studentRemovalTitle'),
		html: confirmationHtml(t, preview),
		icon: 'warning',
		width: 640,
		input: 'text',
		inputPlaceholder: lastName,
		inputAttributes: { autocapitalize: 'off', autocorrect: 'off', autocomplete: 'off' },
		inputValidator: value => (lastNameMatches(value, lastName) ? undefined : t('Input does not match')),
		showCancelButton: true,
		confirmButtonColor: '#d33',
		cancelButtonColor: '#6c757d',
		confirmButtonText: t('studentRemovalConfirm'),
		cancelButtonText: t('Cancel'),
		focusCancel: true
	})

	if (!isConfirmed) return false

	try {
		await http.delete(TApi.ADMIN_REMOVE_STUDENT(user.id))
	} catch {
		return false // Meldung kommt aus shared/http (z. B. 409 mit dem Satz des Servers)
	}

	toast.success(t('studentRemovalDone', { name: fullName(preview.student) }))
	if (typeof onDone === 'function') onDone()
	return true
}
